import { useEffect, useMemo, useState } from "react";
import { CheckSquare, Loader2, Plus, Search } from "lucide-react";
import { useSetPageHeader } from "@/lib/page-header";
import { useAuth } from "@/lib/auth";
import { useFeature } from "@/lib/use-feature";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { ServiceDrawer, type DrawerMode } from "@/components/services/ServiceDrawer";
import { TabelaDeServicos } from "@/components/services/lista/TabelaDeServicos";
import { AcoesEmLote } from "@/components/services/lista/AcoesEmLote";
import { AdicionarProcedimentosDaBase } from "@/components/services/lista/AdicionarProcedimentosDaBase";
import type { ServicoDaLista } from "@/components/services/lista/tipos";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { SERVICOS_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { ehDonoOuSocio } from "@bookdental/schemas";

const secundario =
  "inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 px-3 text-[13px] font-semibold text-zinc-600 transition-colors hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-white/5";

/**
 * Procedimentos da clínica (OfferedService): agrupados por especialidade, preço editável na linha
 * ("Definir preço" quando falta), e "Adicionar procedimento": da base da plataforma, ou criado na hora.
 */
export function Procedimentos() {
  const { user } = useAuth();
  const hasClinicServices = useFeature("clinicServices");
  const org = user?.activeOrganization;
  const canManage = ehDonoOuSocio(org?.role) || !!org?.permissions?.["settings.manage"];
  // Gravar passa por service.update, que exige settings.manage além de prices.manage (e o PIN).
  const podeEditarPreco = canManage && !!org?.permissions?.["prices.manage"];
  const ehClinica = !!org?.types.includes("DENTAL_OFFICE");

  const [busca, setBusca] = useState("");
  const [soSemPreco, setSoSemPreco] = useState(false);
  const [drawer, setDrawer] = useState<DrawerMode>({ kind: "closed" });
  const [daBase, setDaBase] = useState(false);
  const [selecionando, setSelecionando] = useState(false);
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  // Clínica busca na base primeiro; fora dela (não deveria acontecer) cai direto no cadastro.
  const adicionar = () => (ehClinica ? setDaBase(true) : setDrawer({ kind: "create" }));

  useTourDaPagina(SERVICOS_TOUR_STEPS);
  useSetPageHeader({ title: "Procedimentos", subtitle: "Preço, duração e cor do que a clínica faz" }, []);

  const listInput = useMemo(() => ({ search: busca.trim() || undefined, take: 500, skip: 0 }), [busca]);
  const listQuery = trpc.service.list.useQuery(listInput, { enabled: hasClinicServices });
  const ativasQuery = trpc.organization.activeSpecialties.useQuery(undefined, { enabled: hasClinicServices });
  const ativas = useMemo(() => new Set((ativasQuery.data ?? []).map((e) => e.id)), [ativasQuery.data]);
  const todos = (listQuery.data?.services ?? []) as ServicoDaLista[];
  const semPreco = todos.filter((s) => s.status === "ACTIVE" && s.priceCents === 0).length;
  const servicos = soSemPreco ? todos.filter((s) => s.priceCents === 0) : todos;
  useEffect(() => setSelecionados(new Set()), [busca, soSemPreco]);

  const sair = () => {
    setSelecionando(false);
    setSelecionados(new Set());
  };
  const selecionar = (ids: string[], marcar: boolean) =>
    setSelecionados((atual) => {
      const novo = new Set(atual);
      for (const id of ids) {
        if (marcar) novo.add(id);
        else novo.delete(id);
      }
      return novo;
    });

  if (!hasClinicServices) {
    return (
      <div className="mx-auto w-full max-w-[1600px]">
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-zinc-200 py-20 text-center dark:border-border">
          <p className="text-sm font-medium text-zinc-900 dark:text-foreground">Catálogo de procedimentos é um recurso do plano pago</p>
          <p className="mt-1 max-w-sm text-xs text-zinc-500 dark:text-muted-foreground">No plano atual, descreva o procedimento em texto livre direto na consulta.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1600px]">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div data-tour="servicos-busca" className="relative max-w-md flex-1 max-sm:max-w-none max-sm:basis-full">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar..."
            className="h-9 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
          />
        </div>
        {semPreco > 0 && (
          <button
            type="button"
            onClick={() => setSoSemPreco((v) => !v)}
            aria-pressed={soSemPreco}
            className={cn(
              "h-9 rounded-md border px-3 text-[12.5px] font-semibold transition-colors",
              soSemPreco ? "border-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300" : "border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-500/30 dark:text-amber-400",
            )}
          >
            {semPreco} sem preço
          </button>
        )}
        {listQuery.isFetching && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {selecionando ? (
            <AcoesEmLote ids={[...selecionados]} onFim={sair} />
          ) : canManage ? (
            <>
              <button type="button" onClick={() => setSelecionando(true)} className={secundario}>
                <CheckSquare className="h-4 w-4" />
                <span className="hidden sm:inline">Selecionar</span>
              </button>
              {/* Um botão só: o diálogo busca na base e, se não achar, tem o "Criar novo". */}
              <button
                type="button"
                data-tour="servicos-novo"
                onClick={adicionar}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Adicionar procedimento</span>
                <span className="sm:hidden">Adicionar</span>
              </button>
            </>
          ) : null}
        </div>
      </div>

      {/* Com lista, os cartões das especialidades fazem a divisão; o contorno fica só nos estados vazios. */}
      <div
        data-tour="servicos-filtro-categoria"
        className={cn(
          (listQuery.isLoading || servicos.length === 0) &&
            "overflow-hidden rounded-lg border border-zinc-200 bg-white dark:border-border dark:bg-card",
        )}
      >
        {listQuery.isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
          </div>
        ) : servicos.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <p className="text-sm font-medium text-zinc-900 dark:text-foreground">{busca ? "Nenhum resultado" : "Nenhum procedimento ainda"}</p>
            <p className="mt-1 max-w-md text-xs text-zinc-500 dark:text-muted-foreground">
              {busca ? "Tente outro termo de busca." : "Escolha os procedimentos da base da plataforma — o que não estiver lá, você cria na hora."}
            </p>
            {!busca && canManage && (
              <button
                type="button"
                onClick={adicionar}
                className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm hover:bg-brand-700"
              >
                <Plus className="h-4 w-4" />
                Adicionar procedimento
              </button>
            )}
          </div>
        ) : (
          <TabelaDeServicos
            servicos={servicos}
            selecionando={selecionando}
            selecionados={selecionados}
            onSelecionar={selecionar}
            onAbrir={(id) => canManage && setDrawer({ kind: "edit", id })}
            podeEditarPreco={podeEditarPreco}
            ativas={ativas}
          />
        )}
      </div>

      <ServiceDrawer mode={drawer} onClose={() => setDrawer({ kind: "closed" })} onSaved={() => setDrawer({ kind: "closed" })} />
      <AdicionarProcedimentosDaBase open={daBase} onClose={() => setDaBase(false)} onCriarNovo={() => setDrawer({ kind: "create" })} />
    </div>
  );
}
